import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, Icon, Input, Pagination, Select, Table, Text, type TableColumn } from "@app/ui";
import { listAllOrgs } from "../api/dpp.js";
import type { CrossTenantParams } from "../api/platform.js";
import { ErrorAlert } from "../errors/index.js";
import { useT } from "../i18n.js";
import type { OrgWithCounts, Page } from "../types.js";
import { ShowDemoToggle, useShowDemo } from "./useShowDemo.js";

const PAGE_SIZE = 25;

/**
 * Shared shell for the platform console's cross-tenant lists (products,
 * passports): search + org + status filters, server-side paging. Filters live
 * in the URL so a filtered view can be linked (e.g. from an org row).
 */
export function CrossTenantList<T>({
  title,
  fetch,
  columns,
  statuses,
  searchPlaceholder,
  rowKey,
  onRowClick,
  empty,
}: {
  title: string;
  fetch: (p: CrossTenantParams) => Promise<Page<T>>;
  columns: TableColumn<T>[];
  statuses: Array<{ value: string; label: string }>;
  searchPlaceholder: string;
  rowKey: (row: T) => string;
  onRowClick: (row: T) => void;
  empty: string;
}) {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const { showDemo } = useShowDemo();
  const orgId = params.get("orgId") ?? "";
  const status = params.get("status") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [orgs, setOrgs] = useState<OrgWithCounts[]>([]);
  const [data, setData] = useState<Page<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  // Update one URL filter; any filter change resets to the first page.
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    void listAllOrgs(showDemo).then(setOrgs, () => {});
  }, [showDemo]);

  // Debounce typing so each keystroke doesn't hit the API.
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((params.get("q") ?? "") !== search.trim()) setParam("q", search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await fetch({
          search: params.get("q") || undefined,
          orgId: orgId || undefined,
          status: status || undefined,
          includeDemo: showDemo,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        }),
      );
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => void load(), [params.toString(), showDemo]);

  return (
    <Card title={title}>
      {error != null && <ErrorAlert error={error} onRetry={() => void load()} />}
      <div className="flex flex-wrap items-center gap-3" style={{ marginBottom: "var(--az-sp-3)" }}>
        <div style={{ width: 300, maxWidth: "100%" }}>
          <Input
            prefix={<Icon name="search" size={14} />}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ width: 240, maxWidth: "100%" }}>
          <Select
            showSearch
            options={[
              { label: t("platform.allOrgs"), value: "" },
              ...orgs.map((o) => ({ label: o.name, value: o.id, searchText: o.slug })),
            ]}
            value={orgId}
            onChange={(v) => setParam("orgId", v)}
          />
        </div>
        <div style={{ width: 180, maxWidth: "100%" }}>
          <Select
            options={[{ label: t("adminOrgs.allStatuses"), value: "" }, ...statuses]}
            value={status}
            onChange={(v) => setParam("status", v)}
          />
        </div>
        <ShowDemoToggle />
        {data && (
          <Text small secondary>
            {t("common.totalCount", { count: data.total })}
          </Text>
        )}
      </div>
      <Table
        rowKey={rowKey}
        loading={loading && !data}
        empty={empty}
        onRowClick={onRowClick}
        columns={columns}
        data={data?.items ?? []}
      />
      {data && data.total > PAGE_SIZE && (
        <div style={{ marginTop: "var(--az-sp-3)" }}>
          <Pagination
            total={data.total}
            pageSize={PAGE_SIZE}
            current={page}
            onChange={(p) => setParam("page", String(p))}
          />
        </div>
      )}
    </Card>
  );
}
